import { useEffect, useState } from "react";
import { Chip, CircularProgress, TextField, Typography } from "@mui/material";
import { s3baseUrl } from "src/config/config";
import { htmlDecode } from "src/utils/convertHtml";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import CustomPopoverSection from "src/components/MenuOption/CustomPopoverSection";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import { useContentSetting } from "src/Hooks/ContentSettingState";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  delete_folder_api,
  general_folder_list,
} from "src/DAL/Programmes/Programmes";
import MoveProgramme from "./MoveProgramme";
import CustomPopover from "../UserInformation/CustomPopover";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

export default function ProgrammesFolder() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { setProgramTab } = useContentSetting();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filterName, setFilterName] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);

  const {
    data: program = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["programmes-folder"],
    queryFn: async () => {
      const result = await general_folder_list();
      if (result.code === 200) {
        return result.folder;
      } else {
        throw new Error(result.message);
      }
    },
  });

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
  }, [error]);

  const filteredPrograme = program.filter((item) =>
    item.name.toLowerCase().includes(filterName.toLowerCase()),
  );

  const handleNavigateEdit = (value) => {
    navigate(`/programmes-folder/edit-folder/${value?._id}`);
  };

  const handleMoveProgramme = (value) => {
    setModalOpen(true);
    setModalData(value);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
  };

  const handleNavigateCalendar = (value) => {
    navigate(`/programmes/${value?._id}`, {
      state: value,
    });
  };

  const deleteMutation = useMutation({
    mutationFn: async (folderId) => {
      const result = await delete_folder_api(folderId);
      if (result.code !== 200) {
        throw new Error(result.message);
      }
      return result;
    },
    onSuccess: (result) => {
      handleCloseDelete();
      enqueueSnackbar(result.message, { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["programmes-folder"] });
    },
    onError: (mutationError) => {
      enqueueSnackbar(mutationError.message, { variant: "error" });
      handleCloseDelete();
    },
  });

  const handleDelete = async () => {
    if (!deleteDoc?._id) return;
    deleteMutation.mutate(deleteDoc._id);
  };

  const handleOpenDelete = (value) => {
    setDeleteDoc(value);
    setOpenDelete(true);
  };

  useEffect(() => {
    setProgramTab("active");
  }, [setProgramTab]);

  const MENU_OPTIONS = (value) => {
    let array = [
      {
        label: "Edit",
        icon: "akar-icons:edit",
        handleClick: handleNavigateEdit,
      },
      {
        label: "Move Programmes",
        icon: "ic:round-drive-file-move",
        handleClick: handleMoveProgramme,
      },
    ];
    if (!value?.is_default) {
      array.splice(1, 0, {
        label: "Delete",
        icon: "ant-design:delete-twotone",
        handleClick: handleOpenDelete,
      });
    }
    return array;
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  if (isLoading === true) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  const isRecordNotFound = filteredPrograme.length === 0;

  return (
    <div className="container mt-2">
      <CustomConfirmation
        handleOpenDelete={handleOpenDelete}
        setOpen={setOpenDelete}
        title={
          <p className="confirmation-string">
            Are you sure you want to delete this folder ? Deleting it will also
            delete all the programs inside permanently. (Shared programmes in
            this folder will not be deleted, they will be moved to the default
            folder.)
          </p>
        }
        handleCloseDelete={handleCloseDelete}
        handleAgree={handleDelete}
        open={openDelete}
        setOpenDelete={setOpenDelete}
      />
      <div className="row">
        <div className="col-lg-6 col-sm-12">
          <h2>Active Programme Folders</h2>
        </div>
        <div className="col-lg-6 col-sm-12 d-flex justify-content-end">
          <TextField
            fullWidth
            label="Search"
            sx={{ marginRight: 2, width: 240 }}
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
          />
          <button
            onClick={() => navigate(`/programmes-folder/add-folder`)}
            className="small-contained-button"
          >
            Add Folder
          </button>
        </div>
        {filteredPrograme &&
          filteredPrograme.length > 0 &&
          filteredPrograme.map((value, index) => {
            return (
              <div className="col-lg-4 col-md-4 col-sm-12 mb-4" key={index}>
                <div
                  className="card mt-4 pods-cards-shadow cursor h-100 overflow-hidden"
                  onClick={() => handleNavigateCalendar(value)}
                >
                  {value?.is_default && (
                    <span
                      style={{
                        position: "absolute",
                        top: "36px",
                        justifyContent: "center",
                        zIndex: "1",
                        left: "-38px",
                        transform: "rotate(-53deg)",
                      }}
                    >
                      <Chip
                        label={"Default"}
                        variant="contained"
                        color={"warning"}
                        sx={{
                          fontSize: "12px",
                          height: "auto",
                          width: "150px",
                        }}
                      />
                    </span>
                  )}
                  <img
                    src={s3baseUrl + value.image.thumbnail_1}
                    className="card-img-top pods-image"
                    alt="Programme"
                  />
                  <div className="card-body">
                    <div className="row">
                      <div className="col-12">
                        <h3 className="h2-heading">{htmlDecode(value.name)}</h3>
                      </div>

                      <div
                        className="col-2 programmes-menu-option"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <CustomPopoverSection
                          menu={MENU_OPTIONS(value)}
                          data={value}
                        />
                      </div>
                    </div>
                    <p className="programme-card-desc mb-3">
                      {htmlDecode(value.description)}
                    </p>
                    <div className="row recording-card-date mb-2">
                      <div className="card-button recording-card-date-position">
                        <p className="pods-active-members">
                          <span> {value.program_count} Programmes </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        {isRecordNotFound && (
          <Typography
            variant="h5"
            align="center"
            style={{ marginTop: 90 }}
            justifyContent="center"
          >
            No results found
          </Typography>
        )}
      </div>
      <CustomPopover
        isOpenPop={modalOpen}
        width={"500px"}
        isClosePop={setModalOpen}
        title="Move Programmes"
        componentToPassDown={
          <div className="px-4 pb-3">
            <MoveProgramme
              modalData={modalData}
              folder_list={program}
              setModalOpen={setModalOpen}
              reloadList={refetch}
            />
          </div>
        }
      />
    </div>
  );
}
